import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Command, Sun, Moon, Menu, X } from "lucide-react";
import { useTheme } from "../lib/api";

const LINKS = [
    { label: "About", href: "#about" },
    { label: "Work", href: "#projects" },
    { label: "Skills", href: "#skills" },
    { label: "GitHub", href: "#github" },
    { label: "Experience", href: "#experience" },
    { label: "Insights", href: "#blogs" },
    { label: "Workspace", href: "#workspace" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar({ onOpenPalette }) {
    const { theme, toggle } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <>
            <motion.header
                initial={{ y: -80 }} animate={{ y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "py-3" : "py-5"}`}
            >
                <div className={`mx-auto max-w-6xl px-5 flex items-center justify-between rounded-2xl transition-all duration-300 ${scrolled ? "glass py-2.5 px-4 shadow-lg" : ""}`}>
                    <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-accent grid place-items-center text-white text-sm">H</span>
                        <span className="hidden sm:inline">Hariom<span className="text-primary">.</span></span>
                    </button>

                    <nav className="hidden md:flex items-center gap-1">
                        {LINKS.map((l) => (
                            <button key={l.href} data-testid={`nav-${l.label.toLowerCase()}`} onClick={() => go(l.href)}
                                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors">
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button data-testid="open-command-palette" onClick={onOpenPalette}
                            className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground glass rounded-lg px-3 py-1.5 hover:text-foreground transition-colors">
                            <Command className="w-3.5 h-3.5" /> <span className="font-mono">⌘K</span>
                        </button>
                        <button data-testid="theme-toggle" onClick={toggle} aria-label="Toggle theme"
                            className="w-9 h-9 grid place-items-center rounded-lg glass hover:text-primary transition-colors">
                            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="md:hidden w-9 h-9 grid place-items-center rounded-lg glass" aria-label="Menu">
                            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 md:hidden pt-24 px-5 bg-background/95 backdrop-blur-xl">
                        <div className="flex flex-col gap-2">
                            {LINKS.map((l, i) => (
                                <motion.button key={l.href} data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                                    onClick={() => go(l.href)} className="text-left font-display text-3xl py-2 border-b border-border">
                                    {l.label}
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
